import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useDebounced } from '@/components/parameters/useDebounced';
import { ApiError, useApi } from '@/api/client';
import type {
  CustomViewDraft,
  CustomViewIssue,
  CustomViewParameterDefault,
  DynamicReportField
} from '@/types';

/** One column as the form holds it. `rename` is null while the customer keeps the shared header. */
type ColumnDraft = { sourceFieldId: number; rename: string | null; aggregate: boolean };

/** One parameter default as the form holds it: a literal value or a SQL expression, never both. */
type DefaultDraft = {
  parameter: string;
  kind: 'value' | 'sql';
  value: string;
  notes: string;
  isMultiSelect: boolean;
};

/** The report's real parameters (p_Report_ClientDetailGrid) — offered as suggestions, not enforced. */
const knownParameters = ['Country', 'HeadOfficeId', 'DateFilterId', 'FromDate', 'ToDate', 'Product', 'DepartmentId'];

function seedDefaults(viewId: number, all: CustomViewParameterDefault[] | null | undefined): DefaultDraft[] | null {
  // Null means the table could not be read: editing is disabled so a save can never blindly
  // replace rows nobody has seen.
  if (all === null || all === undefined) return null;
  return all.filter(d => d.viewId === viewId).map(d => ({
    parameter: d.parameter,
    kind: d.defaultValueSql ? 'sql' as const : 'value' as const,
    value: d.defaultValueSql ?? d.defaultValue ?? '',
    notes: d.notes ?? '',
    isMultiSelect: d.isMultiSelect ?? false
  }));
}

/**
 * The custom-view builder, for creating (/admin/custom-views/new, optionally ?base=) and editing
 * (/admin/custom-views/:viewId/edit): reshape the columns — remove, reorder, rename, add — with
 * live validation, then apply.
 *
 * Applying calls dbo.usp_ReportPortal_SaveDynamicReportView on MANDW01 through the Database Access
 * API's routine plane (dev mode: an in-memory sandbox with identical rules). The builder's job is
 * the error-prone part the runbook used to leave to hand-written SQL: a rename must become a NEW
 * field row over the same FieldSQL (never an edit of the shared one), renamed fields keep their
 * original position, exact existing renames are reused, and duplicate headers are refused before
 * they can fail the report run.
 */
export function CustomViewBuilder() {
  const api = useApi();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { viewId: viewIdParam } = useParams();
  const editingViewId = viewIdParam ? Number.parseInt(viewIdParam, 10) : null;

  const catalogQ = useQuery({
    queryKey: ['custom-view-catalog'],
    queryFn: api.customViewCatalog
  });
  const catalog = catalogQ.data;

  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [baseViewId, setBaseViewId] = useState<number | null>(null);
  const [columns, setColumns] = useState<ColumnDraft[]>([]);
  const [distinct, setDistinct] = useState(false);
  const [label, setLabel] = useState('');
  const [defaults, setDefaults] = useState<DefaultDraft[] | null>(null);
  const [addSearch, setAddSearch] = useState('');

  const fieldsById = useMemo(() => {
    const map = new Map<number, DynamicReportField>();
    for (const f of catalog?.fields ?? []) map.set(f.fieldId, f);
    return map;
  }, [catalog]);

  // Seed once the catalogue arrives. Editing seeds from the view itself (which is also its own
  // "base" — join-drop warnings then compare against its current shape); creating seeds from
  // ?base=<id> (the library's Clone button), else the view called "Default", else the first live.
  useEffect(() => {
    if (!catalog || baseViewId !== null || catalog.views.length === 0) return;
    const requested = editingViewId ?? Number.parseInt(params.get('base') ?? '', 10);
    const base =
      catalog.views.find(v => v.viewId === requested)
      ?? catalog.views.find(v => v.viewDescription === 'Default')
      ?? catalog.views.find(v => v.live)
      ?? catalog.views[0];
    setBaseViewId(base.viewId);
    setColumns(seedColumns(base));
    setDistinct(base.distinct);
    setDefaults(seedDefaults(base.viewId, catalog.parameterDefaults));
    if (editingViewId !== null) {
      setName(base.viewDescription);
      setNameTouched(true);
      setLabel(base.label ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog]);

  function loadBase(viewId: number) {
    if (!catalog) return;
    const base = catalog.views.find(v => v.viewId === viewId);
    if (!base) return;
    setBaseViewId(viewId);
    setColumns(seedColumns(base));
    setDistinct(base.distinct);
    setDefaults(seedDefaults(viewId, catalog.parameterDefaults));
  }

  const draft: CustomViewDraft | null = useMemo(() => {
    if (baseViewId === null || columns.length === 0) return null;
    return {
      viewDescription: name,
      baseViewId,
      columns: columns.map(c => ({ sourceFieldId: c.sourceFieldId, rename: c.rename, aggregate: c.aggregate })),
      editingViewId,
      distinct,
      // Blank = "same as the view name" on create and "keep the stored one" on edit — an edit must
      // never clobber a Label that deliberately differs from the name (e.g. Detail with UDF).
      label: label.trim() === '' ? null : label.trim(),
      // Null only when the existing defaults could not be read — the API then leaves them alone.
      parameterDefaults: defaults === null ? null : defaults.map(d => ({
        parameter: d.parameter,
        defaultValue: d.kind === 'value' ? d.value : null,
        defaultValueSql: d.kind === 'sql' ? d.value : null,
        notes: d.notes.trim() === '' ? null : d.notes.trim(),
        isMultiSelect: d.kind === 'value' && d.isMultiSelect
      }))
    };
  }, [name, baseViewId, columns, editingViewId, distinct, label, defaults]);

  // Validation runs on a settled draft rather than every keystroke. The endpoint is pure (nothing
  // is written anywhere), so calling it repeatedly is safe by construction.
  const draftKey = useDebounced(draft ? JSON.stringify(draft) : '', 400);
  const validation = useQuery({
    queryKey: ['custom-view-validate', draftKey],
    queryFn: () => api.customViewValidate(JSON.parse(draftKey) as CustomViewDraft),
    enabled: draftKey.length > 0 && catalog?.source !== 'unconfigured',
    placeholderData: keepPreviousData
  });
  const result = validation.data;

  const apply = useMutation({
    mutationFn: (d: CustomViewDraft) =>
      editingViewId === null ? api.createCustomView(d) : api.updateCustomView(editingViewId, d),
    onSuccess: applied =>
      navigate(`/admin/custom-views?saved=${encodeURIComponent(applied.viewDescription)}`)
  });

  if (catalogQ.isLoading) return <div className="placeholder">Loading the ReportData catalogue...</div>;
  if (catalogQ.error) {
    return <div className="error-banner">Could not load the catalogue: {(catalogQ.error as Error).message}</div>;
  }
  if (!catalog) return null;

  const editingMissing = editingViewId !== null && !catalog.views.some(v => v.viewId === editingViewId);
  if (editingMissing) {
    return (
      <>
        <Header editing />
        <div className="error-banner">View {editingViewId} does not exist on this report.</div>
      </>
    );
  }

  if (catalog.source === 'unconfigured') {
    return (
      <>
        <Header editing={editingViewId !== null} />
        <div className="notice notice-warn">
          <strong>The ReportData catalogue could not be read.</strong>
          Nothing can be validated or applied without it — check the Database Access subscription key
          and the caller&rsquo;s grant on <code>reportdata</code>, then reload.
        </div>
      </>
    );
  }

  const baseView = catalog.views.find(v => v.viewId === baseViewId);
  const usedFieldIds = new Set(columns.map(c => c.sourceFieldId));
  // Issues the user has not caused yet are noise: hold back "name missing" until the field was
  // touched. Everything else shows as soon as the server reports it.
  const visibleIssues = (result?.issues ?? []).filter(i => nameTouched || i.code !== 'name_missing');
  const hasErrors = (result?.issues ?? []).some(i => i.severity === 'error');
  const canApply = !!draft && !!result && !hasErrors && !validation.isFetching && !apply.isPending;

  const needle = addSearch.trim().toLowerCase();
  const addable = catalog.fields
    .filter(f => !usedFieldIds.has(f.fieldId))
    .filter(f => !needle
      || f.fieldAlias.toLowerCase().includes(needle)
      || f.fieldSql.toLowerCase().includes(needle)
      || (f.tableAlias ?? '').toLowerCase().includes(needle)
      || String(f.fieldId).includes(needle));

  function move(index: number, delta: number) {
    setColumns(prev => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  return (
    <>
      <Header editing={editingViewId !== null} />

      {catalog.source === 'sample' && (
        <div className="notice notice-warn" style={{ marginBottom: 18 }}>
          <strong>Dev sandbox — MANDW01 not reachable.</strong>
          The flow works end-to-end, but applies to an in-memory copy of a representative catalogue.
        </div>
      )}

      {/* ── 1. Name + base ─────────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header"><h3>1 &middot; Name{editingViewId === null ? ' and base view' : ''}</h3></div>
        <div className="card-body" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 280px' }}>
            <div className="card-label">View name (appears in the report&rsquo;s View dropdown)</div>
            <input
              className="input"
              value={name}
              placeholder="e.g. University of Salford"
              onChange={e => { setName(e.target.value); setNameTouched(true); }}
            />
          </label>
          <label style={{ flex: '1 1 280px' }}>
            <div className="card-label">Report header (Label)</div>
            <input
              className="input"
              value={label}
              placeholder={editingViewId === null ? 'Same as the view name' : 'Keep the current header'}
              title="The heading rendered at the top of the report. Usually the view name, but it can differ — 'Detail with UDF' renders as 'Client Detail - UDF'."
              onChange={e => setLabel(e.target.value)}
            />
          </label>
          {editingViewId === null ? (
            <label style={{ flex: '1 1 280px' }}>
              <div className="card-label">Start from</div>
              <select
                className="input"
                value={baseViewId ?? ''}
                onChange={e => loadBase(Number.parseInt(e.target.value, 10))}
                title="Changing the base reloads the column list below">
                {catalog.views.map(v => (
                  <option key={v.viewId} value={v.viewId}>
                    {v.viewDescription} (ViewId {v.viewId}{v.live ? '' : ', retired'})
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div style={{ flex: '1 1 280px', fontSize: '.85rem', color: 'var(--kt-grey-500)' }}>
              Editing ViewId {editingViewId}. Audience and Distinct are not editable here — the save
              never touches them.
            </div>
          )}
          <label style={{ flex: '1 1 100%', display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '.86rem' }}>
            <input
              type="checkbox"
              checked={distinct}
              onChange={e => {
                setDistinct(e.target.checked);
                // Aggregate marks only mean anything under Distinct, and the column that holds
                // them is hidden without it — clear them so nothing invisible blocks the save.
                if (!e.target.checked) setColumns(prev => prev.map(c => ({ ...c, aggregate: false })));
              }}
            />
            <span>
              <strong>Distinct (aggregated view)</strong> — group identical rows together; columns
              marked <strong>Agg</strong> below are totalled with their field&rsquo;s function
              (SUM for amounts, MAX otherwise) instead of being grouped by.
            </span>
          </label>
          {editingViewId === null && baseView && (
            <div style={{ flex: '1 1 100%', fontSize: '.8rem', color: 'var(--kt-grey-500)' }}>
              The new view inherits <strong>SecurityGroups</strong> from the base
              ({baseView.securityGroups || 'no groups — admins only'}), and copies the base&rsquo;s
              parameter defaults. Changing the base above resets the column list.
            </div>
          )}
        </div>
      </div>

      {/* ── 2. Columns ─────────────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header">
          <h3>2 &middot; Columns ({columns.length})</h3>
        </div>
        <table className="data-table cv-columns">
          <thead>
            <tr>
              <th style={{ width: 34 }}>#</th>
              <th style={{ width: 62 }}>Order</th>
              <th>Header (edit to rename)</th>
              <th>Source field</th>
              {distinct && (
                <th style={{ width: 70 }} title="Aggregate this column (with the field's function, default MAX) instead of grouping by it">
                  Agg
                </th>
              )}
              <th style={{ width: 140 }}>Becomes</th>
              <th style={{ width: 34 }} />
            </tr>
          </thead>
          <tbody>
            {columns.length === 0 && (
              <tr><td colSpan={distinct ? 7 : 6} className="placeholder">No columns — add some below.</td></tr>
            )}
            {columns.map((c, i) => {
              const field = fieldsById.get(c.sourceFieldId);
              const header = c.rename ?? field?.fieldAlias ?? `FieldId ${c.sourceFieldId}?`;
              const resolved = result?.layout.find(l => l.position === i + 1);
              return (
                <tr key={`${c.sourceFieldId}-${i}`}>
                  <td style={{ color: 'var(--kt-grey-400)' }}>{i + 1}</td>
                  <td>
                    <span className="cv-order">
                      <button className="btn-secondary cv-icon" disabled={i === 0}
                        onClick={() => move(i, -1)} title="Move up" aria-label={`Move ${header} up`}>↑</button>
                      <button className="btn-secondary cv-icon" disabled={i === columns.length - 1}
                        onClick={() => move(i, 1)} title="Move down" aria-label={`Move ${header} down`}>↓</button>
                    </span>
                  </td>
                  <td>
                    <input
                      className="input"
                      style={{ width: '100%', minWidth: 140 }}
                      value={header}
                      aria-label={`Header for column ${i + 1}`}
                      onChange={e => setColumns(prev => prev.map((col, idx) => {
                        if (idx !== i) return col;
                        const value = e.target.value;
                        // Typing the original header back is "no rename", not a rename to the same
                        // words — the draft stays clean and no new field row is proposed.
                        return { ...col, rename: value === field?.fieldAlias ? null : value };
                      }))}
                    />
                  </td>
                  <td>
                    {field ? (
                      <span title={field.fieldSql}>
                        {field.fieldAlias} <span style={{ color: 'var(--kt-grey-400)' }}>({field.fieldId})</span>
                        {field.tableAlias && (
                          <span className="chip chip-folder" style={{ marginLeft: 6 }}>{field.tableAlias}</span>
                        )}
                        {field.dateFilter && (
                          <span className="chip chip-folder" style={{ marginLeft: 4 }}
                            title="Also one of the report's @DateFilterId choices — an ordinary column here">
                            date filter
                          </span>
                        )}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--kt-red)' }}>Unknown FieldId {c.sourceFieldId}</span>
                    )}
                  </td>
                  {distinct && (
                    <td>
                      <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}
                        title={`Aggregated with ${field?.aggregateFunction ?? 'MAX (default)'}`}>
                        <input
                          type="checkbox"
                          checked={c.aggregate}
                          aria-label={`Aggregate ${header}`}
                          onChange={e => setColumns(prev => prev.map((col, idx) =>
                            idx === i ? { ...col, aggregate: e.target.checked } : col))}
                        />
                        {c.aggregate && (
                          <span style={{ fontSize: '.72rem', color: 'var(--kt-grey-500)' }}>
                            {field?.aggregateFunction ?? 'MAX'}
                          </span>
                        )}
                      </label>
                    </td>
                  )}
                  <td><Becomes rename={c.rename} resolved={resolved} /></td>
                  <td>
                    <button className="btn-secondary cv-icon" title={`Remove ${header}`}
                      aria-label={`Remove ${header}`}
                      onClick={() => setColumns(prev => prev.filter((_, idx) => idx !== i))}>
                      ✕
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="card-body" style={{ borderTop: '1px solid var(--kt-grey-200)' }}>
          <div className="card-label">Add a column</div>
          <input
            className="input"
            style={{ maxWidth: 420, marginBottom: 8 }}
            type="search"
            placeholder="Search fields by header, FieldId, table or SQL..."
            value={addSearch}
            onChange={e => setAddSearch(e.target.value)}
          />
          <div className="chip-row">
            {addable.slice(0, 24).map(f => (
              <button
                key={f.fieldId}
                className="chip"
                style={{ cursor: 'pointer' }}
                title={`${f.tableAlias ?? ''} ${f.fieldSql}${f.notes ? `\n${f.notes}` : ''}`.trim()}
                onClick={() => setColumns(prev => [...prev, { sourceFieldId: f.fieldId, rename: null, aggregate: false }])}>
                + {f.fieldAlias} <span style={{ color: 'var(--kt-grey-400)' }}>({f.fieldId})</span>
              </button>
            ))}
            {addable.length > 24 && (
              <span className="chip chip-folder">…{addable.length - 24} more — narrow the search</span>
            )}
            {addable.length === 0 && (
              <span className="chip chip-folder">
                {needle ? 'No field matches.' : 'Every field is already in the view.'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── 3. Parameter defaults ──────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header">
          <h3>3 &middot; Parameter defaults{defaults !== null && <> ({defaults.length})</>}</h3>
          {defaults !== null && (
            <button className="btn-secondary cv-btn"
              onClick={() => setDefaults(prev => [...(prev ?? []),
                { parameter: '', kind: 'value', value: '', notes: '', isMultiSelect: false }])}>
              Add default
            </button>
          )}
        </div>
        {defaults === null ? (
          <div className="card-body notice notice-warn" style={{ margin: 0, border: 0 }}>
            <strong>The existing defaults could not be read</strong>, so editing them here is
            disabled — saving would otherwise replace rows nobody has seen. The save leaves the
            view&rsquo;s stored defaults untouched.
          </div>
        ) : defaults.length === 0 ? (
          <div className="card-body" style={{ fontSize: '.85rem', color: 'var(--kt-grey-500)' }}>
            None — the report asks for everything as usual. Add one to pre-fill a parameter for
            this view (e.g. the client&rsquo;s HeadOfficeId, or a relative date window as SQL).
          </div>
        ) : (
          <table className="data-table cv-columns">
            <thead>
              <tr>
                <th style={{ width: 150 }}>Parameter</th>
                <th style={{ width: 110 }}>Kind</th>
                <th>Default</th>
                <th style={{ width: 200 }}>Notes (shown in this library)</th>
                <th style={{ width: 56 }} title="Comma-separated list of values">Multi</th>
                <th style={{ width: 34 }} />
              </tr>
            </thead>
            <tbody>
              {defaults.map((d, i) => (
                <tr key={i}>
                  <td>
                    <input className="input" style={{ width: '100%' }} list="cv-known-parameters"
                      value={d.parameter} placeholder="e.g. HeadOfficeId"
                      aria-label={`Default ${i + 1} parameter`}
                      onChange={e => setDefaults(prev => prev!.map((row, idx) =>
                        idx === i ? { ...row, parameter: e.target.value } : row))}
                    />
                  </td>
                  <td>
                    <select className="input" value={d.kind}
                      aria-label={`Default ${i + 1} kind`}
                      title="A literal value, or a SQL expression evaluated at run time (relative dates)"
                      onChange={e => setDefaults(prev => prev!.map((row, idx) =>
                        idx === i ? { ...row, kind: e.target.value as 'value' | 'sql' } : row))}>
                      <option value="value">Value</option>
                      <option value="sql">SQL expression</option>
                    </select>
                  </td>
                  <td>
                    <input className="input"
                      style={{ width: '100%', fontFamily: d.kind === 'sql' ? 'monospace' : undefined }}
                      value={d.value}
                      placeholder={d.kind === 'sql' ? 'e.g. CAST(DATEADD(WEEK, …, 0) AS date)' : 'e.g. 201143'}
                      aria-label={`Default ${i + 1} value`}
                      onChange={e => setDefaults(prev => prev!.map((row, idx) =>
                        idx === i ? { ...row, value: e.target.value } : row))}
                    />
                  </td>
                  <td>
                    <input className="input" style={{ width: '100%' }} value={d.notes}
                      placeholder={d.kind === 'sql' ? 'e.g. Start of next week (Mon)' : ''}
                      aria-label={`Default ${i + 1} notes`}
                      onChange={e => setDefaults(prev => prev!.map((row, idx) =>
                        idx === i ? { ...row, notes: e.target.value } : row))}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={d.isMultiSelect} disabled={d.kind === 'sql'}
                      aria-label={`Default ${i + 1} multi-select`}
                      onChange={e => setDefaults(prev => prev!.map((row, idx) =>
                        idx === i ? { ...row, isMultiSelect: e.target.checked } : row))}
                    />
                  </td>
                  <td>
                    <button className="btn-secondary cv-icon" title="Remove this default"
                      aria-label={`Remove default ${i + 1}`}
                      onClick={() => setDefaults(prev => prev!.filter((_, idx) => idx !== i))}>
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <datalist id="cv-known-parameters">
          {knownParameters.map(p => <option key={p} value={p} />)}
        </datalist>
      </div>

      {/* ── 4. Checks + preview ────────────────────────────────────────────────────── */}
      {visibleIssues.length > 0 && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-header"><h3>4 &middot; Checks</h3></div>
          <div className="card-body">
            {visibleIssues.map((issue, i) => <Issue key={`${issue.code}-${i}`} issue={issue} />)}
          </div>
        </div>
      )}

      {result && result.layout.length > 0 && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-header"><h3>{visibleIssues.length > 0 ? 5 : 4} &middot; Layout preview</h3></div>
          <div className="card-body">
            <div style={{ fontSize: '.8rem', color: 'var(--kt-grey-500)', marginBottom: 6 }}>
              The columns as the report will render them, in order:
            </div>
            <div className="chip-row">
              {result.layout.map(col => (
                <span key={col.position} className="chip"
                  title={col.copyOfFieldId != null
                    ? `New field row, copy of FieldId ${col.copyOfFieldId}`
                    : `FieldId ${col.fieldId}${col.reusedExistingField ? ' (reused existing rename row)' : ''}`}>
                  {col.position}. {col.header}
                  {col.aggregate && <span style={{ color: 'var(--kt-green)' }}> Σ</span>}
                  {col.copyOfFieldId != null && <span style={{ color: 'var(--kt-orange)' }}> *</span>}
                </span>
              ))}
            </div>
            {result.layout.some(c => c.copyOfFieldId != null) && (
              <div style={{ fontSize: '.78rem', color: 'var(--kt-grey-500)', marginTop: 6 }}>
                * a new shared field row is created on save; the original is left untouched
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Apply ──────────────────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-body" style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn-primary" disabled={!canApply} onClick={() => draft && apply.mutate(draft)}>
            {apply.isPending
              ? 'Applying…'
              : editingViewId === null ? 'Create view' : 'Save changes'}
          </button>
          <div style={{ fontSize: '.82rem', color: 'var(--kt-grey-500)' }}>
            {validation.isFetching
              ? 'Checking the draft…'
              : hasErrors
                ? 'Fix the errors above first.'
                : editingViewId === null
                  ? 'Creates the view live in the report’s View dropdown.'
                  : 'Replaces the view’s name and column list.'}
          </div>
          {apply.isError && (
            <div className="error-banner" style={{ flex: '1 1 100%' }}>
              {describeApplyError(apply.error)}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Header({ editing }: { editing: boolean }) {
  return (
    <div className="page-header">
      <div>
        <h1>{editing ? 'Edit custom view' : 'New custom view'}</h1>
        <div className="sub">
          {editing
            ? 'Rework the view’s name and columns, then save. Changes apply to the report server’s configuration.'
            : 'Compose a customer’s Any Client Detail view and create it directly. Renames never touch the shared originals — they become new field rows.'}
        </div>
      </div>
      <Link to="/admin/custom-views" className="btn-secondary">Back to view library</Link>
    </div>
  );
}

function Becomes({ rename, resolved }: {
  rename: string | null;
  resolved?: { copyOfFieldId?: number | null; fieldId?: number | null; reusedExistingField: boolean };
}) {
  if (rename === null) return <span style={{ color: 'var(--kt-grey-400)' }}>as-is</span>;
  if (resolved?.reusedExistingField) {
    return <span className="pill pill-success" title="An identical rename row already exists and is reused — nothing new is inserted">
      Reuses {resolved.fieldId}
    </span>;
  }
  if (resolved?.copyOfFieldId != null) {
    return <span className="pill pill-pending" title="Saving inserts a new shared field row over the same FieldSQL">
      New field row
    </span>;
  }
  return <span className="pill pill-pending">Rename</span>;
}

function Issue({ issue }: { issue: CustomViewIssue }) {
  const pill = issue.severity === 'error' ? 'pill-failed' : issue.severity === 'warning' ? 'pill-pending' : 'pill-unknown';
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
      <span className={`pill ${pill}`} style={{ flexShrink: 0 }}>{issue.severity}</span>
      <div style={{ fontSize: '.86rem' }}>{issue.message}</div>
    </div>
  );
}

/**
 * A refused apply carries the composer's issue list in the problem response's `data`; show the
 * messages rather than the single-sentence summary when they are there.
 */
function describeApplyError(error: unknown): string {
  if (error instanceof ApiError && Array.isArray(error.data)) {
    const issues = error.data as CustomViewIssue[];
    const errors = issues.filter(i => i.severity === 'error').map(i => i.message);
    if (errors.length > 0) return errors.join(' ');
  }
  return error instanceof Error ? error.message.replace(/^API \d+: /, '') : 'The apply failed.';
}

function seedColumns(view: { fieldIdList: string; aggregateFieldIdList?: string | null }): ColumnDraft[] {
  const aggregated = new Set(parseIds(view.aggregateFieldIdList ?? ''));
  return parseIds(view.fieldIdList)
    .map(id => ({ sourceFieldId: id, rename: null, aggregate: aggregated.has(id) }));
}

function parseIds(list: string): number[] {
  return list
    .split(',')
    .map(t => Number.parseInt(t.trim(), 10))
    .filter(n => Number.isFinite(n));
}
